import { create } from 'zustand'

const NOTICE_DISMISS_MS = 4000

let dismissTimer: ReturnType<typeof setTimeout> | null = null

function clearDismissTimer(): void {
  if (dismissTimer !== null) {
    clearTimeout(dismissTimer)
    dismissTimer = null
  }
}

interface TimelineFeedbackState {
  /** Transient timeline notice (shot detection, rearrangement, clipboard); null when hidden. */
  notice: string | null
  /** Clips with a shot-detection request in flight (drives TimelineClip detecting overlay). */
  detectingShotClipIds: ReadonlySet<string>
  /** Show text and auto-dismiss after 4 s; null clears immediately. Replaces any pending dismiss. */
  flashNotice: (text: string | null) => void
  /** Show text with no auto-dismiss; the next flashNotice replaces it. */
  holdNotice: (text: string) => void
  setDetectingShots: (clipId: string, detecting: boolean) => void
  /** Drop every notice and detecting overlay; a project switch calls it, since both describe the old timeline. */
  reset: () => void
}

export const useTimelineFeedbackStore = create<TimelineFeedbackState>(set => ({
  notice: null,
  detectingShotClipIds: new Set<string>(),
  flashNotice: text => {
    clearDismissTimer()
    set({ notice: text })
    if (text) {
      dismissTimer = setTimeout(() => {
        dismissTimer = null
        set({ notice: null })
      }, NOTICE_DISMISS_MS)
    }
  },
  holdNotice: text => {
    clearDismissTimer()
    set({ notice: text })
  },
  reset: () => {
    clearDismissTimer()
    set({ notice: null, detectingShotClipIds: new Set<string>() })
  },
  setDetectingShots: (clipId, detecting) => {
    set(state => {
      const next = new Set(state.detectingShotClipIds)
      if (detecting) next.add(clipId)
      else next.delete(clipId)
      return { detectingShotClipIds: next }
    })
  },
}))
